import { fn } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { PrismInput, PrismSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type { NewIssue } from 'lumen/controllers/market/parity/parity-manager/addissue';
import dayjsFormat from 'lumen/helpers/dayjs-format';

const META_SOURCES = [
  { id: 'tripadvisor', name: 'Tripadvisor' },
  { id: 'trivago', name: 'Trivago' },
  { id: 'googlehotelfinder', name: 'Google' },
];

interface MetaSourceOption {
  label: string;
  metaSource: (typeof META_SOURCES)[number];
}

interface ParityParityManagerMetaRateFormSignature {
  Args: {
    back: () => void;
    currencyCode: string | undefined;
    issue: NewIssue;
    next: () => void;
    setIssueProperty: (property: string, newValue: unknown) => void;
    setIssuePropertyFromEvent: (property: string, event: Event) => void;
  };
}

export default class ParityParityManagerMetaRateForm extends Component<ParityParityManagerMetaRateFormSignature> {
  get isFormValid(): boolean {
    return Boolean(this.issueMetaSource && this.isMetaRateValid);
  }

  get isMetaRateValid(): boolean {
    return Boolean(
      this.issueBaseRate &&
      this.issueMetaRate &&
      parseFloat(this.issueMetaRate) > 0 &&
      parseFloat(this.issueMetaRate) < parseFloat(this.issueBaseRate),
    );
  }

  get issueBaseRate(): string | undefined {
    return this.args.issue?.base_rate;
  }

  get issueMetaRate(): string | undefined {
    return this.args.issue?.meta_rate;
  }

  get issueMetaSource(): NewIssue['metasearch_channel'] {
    return this.args.issue?.metasearch_channel;
  }

  get metaSourceOptions(): MetaSourceOption[] {
    return META_SOURCES.map((metaSource) => ({
      label: metaSource.name,
      metaSource,
    }));
  }

  get metaSourceValue(): MetaSourceOption | undefined {
    return this.metaSourceOptions.find(
      (option) => option.metaSource === this.issueMetaSource,
    );
  }

  @action
  handleMetaSourceChange(option: MetaSourceOption): void {
    this.args.setIssueProperty('metasearch_channel', option.metaSource);
  }

  <template>
    <div class="pt-400 pr-500 pb-800 pl-500" id="add-issue-form">
      <h2 class="header-title">{{t "parity_manager.add_issue.meta_info"}}</h2>
      <div class="mt-400 flex w-full flex-col">
        <label class="input-label required-indicator">
          {{t "parity_manager.meta_source"}}
        </label>
        <PrismSelectInput
          @onChange={{this.handleMetaSourceChange}}
          @options={{this.metaSourceOptions}}
          @placeholder={{t "general.dropdown.meta_search"}}
          @value={{this.metaSourceValue}}
        />
      </div>

      <div class="mt-400 flex w-full flex-col">
        <PrismInput
          @errorMessage={{t "parity_manager.validation.channel_rate"}}
          @hasErrors={{not this.isMetaRateValid}}
          @inlineLabel={{@currencyCode}}
          @label={{t "parity_manager.meta_rate"}}
          @onInput={{fn @setIssuePropertyFromEvent "meta_rate"}}
          @required={{true}}
          @requiredErrorMessage={{t "general.required"}}
          @value={{@issue.meta_rate}}
        />
      </div>

      <div class="flex w-full flex-col">
        <PrismInput
          @label={{t "parity_manager.meta_room_name"}}
          @onInput={{fn @setIssuePropertyFromEvent "meta_room_name"}}
          @value={{@issue.meta_room_name}}
        />
      </div>

      <div class="flex flex-row">
        <div class="flex w-1/2 flex-col pr-400">
          <label class="input-label required-indicator">{{t
              "general.arrival_date"
            }}</label>
          <PrismInput
            @disabled={{true}}
            @type="text"
            {{! @glint-expect-error: Argument of type 'string | Dayjs | null' is not assignable to parameter of type 'Dayjs | undefined'. }}
            @value={{dayjsFormat @issue.arrival_date format="MM/DD/YYYY"}}
          />
        </div>
        <div class="flex w-1/2 flex-col">
          <label class="input-label required-indicator">{{t
              "rates.los"
            }}</label>
          <PrismInput @disabled={{true}} @type="text" @value={{@issue.los}} />
        </div>
      </div>
    </div>
    <div class="settings-modal__footer flex flex-row justify-end">
      <GeneralFormFieldsCancelAndConfirm
        @cancel={{@back}}
        @cancelText={{t "general.back"}}
        @confirm={{@next}}
        @confirmText={{t "general.next_step"}}
        @isConfirmDisabled={{not this.isFormValid}}
      />
    </div>
  </template>
}
